import {
  type BasePrayerItem,
  type BasePrayerListItem,
  type Hashtag,
  type PrayerItem,
  type PrayerListItem,
  type SetHtRequest,
  type SyncedHashtag,
  type SyncedPrayerItem,
  type SyncedPrayerListItem,
} from '@soulhx/fs-common';
import { v4 as uuid } from 'uuid';
import {
  db,
  type ItemSyncStatus,
  type LocalPrayer,
  type LocalPrayerHashtag,
  type LocalPrayerListItem,
} from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { isItemGoneError, syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, nowUtcIso, pullSince, toUtcIso } from './timestamps.js';

export const PRAYER_SYNC_POLL_INTERVAL_MS = 20_000;
export const PRAYER_SYNC_KEY = 'prayers';

const PRAYER_PATH = '/pray';
const HASHTAG_SYNC_CURSOR_KEY = 'prayerHashtags';
const LIST_ITEM_SYNC_CURSOR_KEY = 'prayerListItems';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const now = nowUtcIso;

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`prayer sync: ${context}`, error);
};

/**
 * Resolves hashtag ids (e.g. from a `LocalPrayer.hashtagIds`) against the
 * locally-synced prayer hashtag catalog. Any id not yet known locally is
 * silently dropped rather than thrown - callers render whatever resolves.
 */
export const resolvePrayerHashtagIds = async (ids: string[]): Promise<Hashtag[]> => {
  if (ids.length === 0) return [];

  const rows = await db.prayerHashtags.bulkGet(ids);

  return rows
    .filter((row): row is LocalPrayerHashtag => row !== undefined)
    .map((row) => ({ id: row.id, tag: row.tag }));
};

/**
 * Same as PassageSync.ts's equivalent, against the prayer hashtag catalog:
 * resolves tag text to ids, creating a `pendingCreate` placeholder row for
 * any tag text not already known so a brand-new tag can be attached fully
 * offline. Placeholders are resolved via the owning prayer's tag push.
 */
const resolveOrCreateLocalPrayerHashtagIds = async (tags: string[]): Promise<string[]> => {
  const fixedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
  const ids: string[] = [];

  await db.transaction('rw', db.prayerHashtags, async () => {
    for (const tag of fixedTags) {
      const existing = await db.prayerHashtags.filter((row) => row.tag === tag).first();

      if (existing) {
        ids.push(existing.id);
        continue;
      }

      const placeholder: LocalPrayerHashtag = { id: uuid(), tag, syncStatus: 'pendingCreate' };
      await db.prayerHashtags.add(placeholder);
      ids.push(placeholder.id);
    }
  });

  return ids;
};

/**
 * Drops any `pendingCreate` placeholder hashtag among `tagIds` that no
 * local prayer references anymore - placeholders are only ever resolved via
 * their prayer's tag push, so one left unreferenced would linger forever.
 * Must be called inside a transaction covering prayers + prayerHashtags.
 */
const dropUnreferencedPlaceholders = async (tagIds: string[]): Promise<void> => {
  for (const tagId of tagIds) {
    const tagRow = await db.prayerHashtags.get(tagId);
    if (tagRow?.syncStatus !== 'pendingCreate') continue;

    const stillReferenced = await db.prayers.filter((p) => (p.hashtagIds ?? []).includes(tagId)).count();
    if (stillReferenced === 0) await db.prayerHashtags.delete(tagId);
  }
};

/**
 * Removes a prayer and all of its list items locally (the server cascades
 * a list's delete to its items), plus any placeholder hashtag only it used.
 */
const dropPrayerLocally = async (id: string): Promise<void> => {
  await db.transaction('rw', db.prayers, db.prayerListItems, db.prayerHashtags, async () => {
    const existing = await db.prayers.get(id);
    await db.prayers.delete(id);
    await db.prayerListItems.where('prayerListId').equals(id).delete();
    await dropUnreferencedPlaceholders(existing?.hashtagIds ?? []);
  });
};

/**
 * Same algorithm as PassageSync.ts's `pushPassageTags` (see its comments
 * for the full rationale): pushes the full desired tag list via `setTags`,
 * upserts the server's canonical rows, repoints any other local prayer
 * still referencing a now-superseded placeholder, and marks the prayer
 * synced.
 */
const pushPrayerTags = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  // re-read rather than trusting `local.hashtagIds` - an earlier prayer in
  // the same pass may have repointed a placeholder this one references
  const current = await db.prayers.get(local.id);
  const localIds = current?.hashtagIds ?? [];
  const localHashtags = await db.prayerHashtags.bulkGet(localIds);
  const tagTexts = localHashtags.filter((row): row is LocalPrayerHashtag => row !== undefined).map((row) => row.tag);

  const body: SetHtRequest = { tags: tagTexts };
  const canonical = await syncFetch<Hashtag[]>({
    method: 'POST',
    path: `${PRAYER_PATH}/${local.id}/setTags`,
    body,
    keepalive,
  });

  await db.transaction('rw', db.prayers, db.prayerHashtags, async () => {
    const canonicalIds: string[] = [];

    for (const ht of canonical) {
      canonicalIds.push(ht.id);

      const existingRow = await db.prayerHashtags.get(ht.id);
      if (!existingRow || !isUnsynced(existingRow.syncStatus)) {
        await db.prayerHashtags.put({ id: ht.id, tag: ht.tag, syncStatus: 'synced' });
      }
    }

    for (const oldId of localIds) {
      if (canonicalIds.includes(oldId)) continue;

      const oldRow = await db.prayerHashtags.get(oldId);
      if (!oldRow) continue;

      const canonicalMatch = canonical.find((ht) => ht.tag === oldRow.tag);
      if (!canonicalMatch) continue;

      const referencing = await db.prayers.filter((p) => (p.hashtagIds ?? []).includes(oldId)).toArray();
      for (const p of referencing) {
        const nextIds = Array.from(new Set(p.hashtagIds!.map((id) => (id === oldId ? canonicalMatch.id : id))));
        await db.prayers.update(p.id, { hashtagIds: nextIds });
      }

      await db.prayerHashtags.delete(oldId);
    }

    await db.prayers.update(local.id, {
      hashtagIds: canonicalIds.length > 0 ? canonicalIds : undefined,
      syncStatus: 'synced',
    });
  });
};

const prayerUpdateBody = (local: LocalPrayer): PrayerItem => ({
  id: local.id,
  text: local.text,
  isList: local.isList,
  completed: local.completed,
  date: local.date,
  targetDate: local.targetDate,
});

const pushPrayerUpdateFields = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  const serverItem = await syncFetch<PrayerItem>({
    method: 'PUT',
    path: `${PRAYER_PATH}/${local.id}`,
    body: prayerUpdateBody(local),
    keepalive,
  });

  await db.prayers.update(local.id, {
    date: toUtcIso(serverItem.date),
    updateDate: serverItem.updateDate ? toUtcIso(serverItem.updateDate) : local.updateDate,
  });
};

const pushPrayerCreate = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  const body: BasePrayerItem = { id: local.id, text: local.text, isList: local.isList, targetDate: local.targetDate };
  const serverItem = await syncFetch<PrayerItem>({ method: 'POST', path: `${PRAYER_PATH}/`, body, keepalive });

  await db.prayers.update(local.id, {
    date: toUtcIso(serverItem.date),
    updateDate: serverItem.updateDate ? toUtcIso(serverItem.updateDate) : local.updateDate,
  });

  // the create endpoint always starts an item uncompleted - an item
  // completed locally before its first push needs a follow-up update
  if (local.completed && !serverItem.completed) {
    await pushPrayerUpdateFields(local, keepalive);
  }

  await pushPrayerTags(local, keepalive);
};

const pushPrayerUpdate = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  await pushPrayerUpdateFields(local, keepalive);
  await pushPrayerTags(local, keepalive);
};

const pushPrayerDelete = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${PRAYER_PATH}/${local.id}`, keepalive });
  await dropPrayerLocally(local.id);
};

const listItemUpdateBody = (local: LocalPrayerListItem): PrayerListItem => ({
  id: local.id,
  itemText: local.itemText,
  prayerListId: local.prayerListId,
  completed: local.completed,
  updateDate: local.updateDate,
  targetDate: local.targetDate,
});

const pushListItemUpdateFields = async (local: LocalPrayerListItem, keepalive?: boolean): Promise<void> => {
  const serverItem = await syncFetch<PrayerListItem>({
    method: 'PUT',
    path: `${PRAYER_PATH}/pli/${local.id}`,
    body: listItemUpdateBody(local),
    keepalive,
  });

  await db.prayerListItems.update(local.id, { updateDate: toUtcIso(serverItem.updateDate), syncStatus: 'synced' });
};

const pushListItemCreate = async (local: LocalPrayerListItem, keepalive?: boolean): Promise<void> => {
  const body: BasePrayerListItem = {
    id: local.id,
    itemText: local.itemText,
    prayerListId: local.prayerListId,
    targetDate: local.targetDate,
  };
  const serverItem = await syncFetch<PrayerListItem>({
    method: 'POST',
    path: `${PRAYER_PATH}/${local.prayerListId}/listItems`,
    body,
    keepalive,
  });

  if (local.completed && !serverItem.completed) {
    await pushListItemUpdateFields({ ...local, updateDate: toUtcIso(serverItem.updateDate) }, keepalive);
    return;
  }

  await db.prayerListItems.update(local.id, { updateDate: toUtcIso(serverItem.updateDate), syncStatus: 'synced' });
};

const pushListItemDelete = async (local: LocalPrayerListItem, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${PRAYER_PATH}/pli/${local.id}`, keepalive });
  await db.prayerListItems.delete(local.id);
};

const pushOnePrayer = async (local: LocalPrayer, keepalive?: boolean): Promise<void> => {
  try {
    switch (local.syncStatus ?? 'pendingCreate') {
      case 'pendingCreate':
        await pushPrayerCreate(local, keepalive);
        break;
      case 'pendingUpdate':
        await pushPrayerUpdate(local, keepalive);
        break;
      case 'pendingDelete':
        await pushPrayerDelete(local, keepalive);
        break;
    }
  } catch (error) {
    // the prayer no longer exists server-side (e.g. deleted on another
    // device while this edit was pending) - see `isItemGoneError`. Left
    // pending it would fail on every cycle forever, since a pull never
    // overwrites an unsynced row.
    if (isItemGoneError(error)) {
      await dropPrayerLocally(local.id);
      return;
    }

    // leave the row's syncStatus untouched so it's retried on the next cycle
    logSyncError(`failed to push prayer ${local.id}`, error);
  }
};

const pushOneListItem = async (local: LocalPrayerListItem, keepalive?: boolean): Promise<void> => {
  // a brand-new list whose own create failed earlier in this pass doesn't
  // exist server-side yet - pushing its items now would 404 and (being
  // indistinguishable from "list deleted elsewhere") drop them. Wait for
  // the list to land first.
  const parent = await db.prayers.get(local.prayerListId);
  if (parent?.syncStatus === 'pendingCreate') return;

  try {
    switch (local.syncStatus ?? 'pendingCreate') {
      case 'pendingCreate':
        await pushListItemCreate(local, keepalive);
        break;
      case 'pendingUpdate':
        await pushListItemUpdateFields(local, keepalive);
        break;
      case 'pendingDelete':
        await pushListItemDelete(local, keepalive);
        break;
    }
  } catch (error) {
    // the item (or its whole list) no longer exists server-side
    if (isItemGoneError(error)) {
      await db.prayerListItems.delete(local.id);
      return;
    }

    logSyncError(`failed to push prayer list item ${local.id}`, error);
  }
};

/**
 * Pushes pending prayers and list items, ordered so that dependencies land
 * first:
 *  1. prayer creates/updates - a brand-new list must exist before its items
 *  2. list item creates/updates/deletes
 *  3. prayer deletes - an offline attach (see `queueLocalAttachPrayerToList`)
 *     is a list item create plus a prayer delete, so the new list item
 *     lands before its source prayer goes away
 */
export const pushPendingPrayers = async (options?: { keepalive?: boolean }): Promise<void> => {
  const keepalive = options?.keepalive;

  const pendingPrayers = await db.prayers.filter((p) => isUnsynced(p.syncStatus)).toArray();
  for (const local of pendingPrayers.filter((p) => p.syncStatus !== 'pendingDelete')) {
    await pushOnePrayer(local, keepalive);
  }

  const pendingListItems = await db.prayerListItems.filter((li) => isUnsynced(li.syncStatus)).toArray();
  for (const local of pendingListItems) {
    await pushOneListItem(local, keepalive);
  }

  for (const local of pendingPrayers.filter((p) => p.syncStatus === 'pendingDelete')) {
    await pushOnePrayer(local, keepalive);
  }
};

export const pullPrayerHashtags = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(HASHTAG_SYNC_CURSOR_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverHashtags, horizon } = await syncPull<SyncedHashtag[]>(
    `${PRAYER_PATH}/ht/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.prayerHashtags, db.syncMeta, async () => {
    for (const serverHashtag of serverHashtags) {
      const updateDate = toUtcIso(serverHashtag.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverHashtag.deleted) {
        await db.prayerHashtags.delete(serverHashtag.id);
        continue;
      }

      const local = await db.prayerHashtags.get(serverHashtag.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPrayerHashtag = {
        id: serverHashtag.id,
        tag: serverHashtag.tag,
        updateDate,
        deleted: false,
        syncStatus: 'synced',
      };
      await db.prayerHashtags.put(toStore);
    }

    await db.syncMeta.put({
      itemId: HASHTAG_SYNC_CURSOR_KEY,
      lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon),
    });
  });
};

export const pullPrayers = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(PRAYER_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverPrayers, horizon } = await syncPull<SyncedPrayerItem[]>(
    `${PRAYER_PATH}/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.prayers, db.prayerListItems, db.syncMeta, async () => {
    for (const serverPrayer of serverPrayers) {
      const updateDate = toUtcIso(serverPrayer.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverPrayer.deleted) {
        // the server cascades a list's delete to its items - mirror that
        // here rather than waiting on each item's own tombstone
        await db.prayers.delete(serverPrayer.id);
        await db.prayerListItems.where('prayerListId').equals(serverPrayer.id).delete();
        continue;
      }

      const local = await db.prayers.get(serverPrayer.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPrayer = {
        ...serverPrayer,
        date: toUtcIso(serverPrayer.date),
        updateDate,
        syncStatus: 'synced',
      };
      await db.prayers.put(toStore);
    }

    await db.syncMeta.put({ itemId: PRAYER_SYNC_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

export const pullPrayerListItems = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(LIST_ITEM_SYNC_CURSOR_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverItems, horizon } = await syncPull<SyncedPrayerListItem[]>(
    `${PRAYER_PATH}/pli/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.prayerListItems, db.syncMeta, async () => {
    for (const serverItem of serverItems) {
      const updateDate = toUtcIso(serverItem.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverItem.deleted) {
        await db.prayerListItems.delete(serverItem.id);
        continue;
      }

      const local = await db.prayerListItems.get(serverItem.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPrayerListItem = { ...serverItem, updateDate, syncStatus: 'synced' };
      await db.prayerListItems.put(toStore);
    }

    await db.syncMeta.put({
      itemId: LIST_ITEM_SYNC_CURSOR_KEY,
      lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon),
    });
  });
};

/**
 * True once all three prayer collections have completed at least one pull
 * (each has a cursor). A page that loads its list once - rather than as a
 * live query - can depend on this to reload when a brand-new login's first
 * sync lands, without also reloading on every later sync.
 */
export const hasPulledPrayerData = async (): Promise<boolean> => {
  const cursors = await db.syncMeta.bulkGet([PRAYER_SYNC_KEY, HASHTAG_SYNC_CURSOR_KEY, LIST_ITEM_SYNC_CURSOR_KEY]);
  return cursors.every((cursor) => cursor !== undefined);
};

/**
 * Pushes any pending local prayer/list item mutations, then pulls whatever
 * changed on the server since the last sync - hashtag catalog first (so a
 * prayer referencing a brand-new tag id resolves), then prayers, then list
 * items. Push-before-pull for the same reason as PassageSync.ts.
 */
export const syncPrayers = async (): Promise<void> => {
  await pushPendingPrayers();
  await pullPrayerHashtags();
  await pullPrayers();
  await pullPrayerListItems();
};

// mirrors the server's `updatePrayerTimestamp`: any list item change bumps
// its list's `date` and `updateDate` (the sort key). Local-only - not a
// pending change of its own; the next pull brings the server's real values.
const touchListDate = async (listId: string): Promise<void> => {
  const timestamp = now();
  await db.prayers.update(listId, { date: timestamp, updateDate: timestamp });
};

/**
 * Local half of deleting a prayer (no trigger) - shared by
 * `queueLocalPrayerDelete` and `queueLocalAttachPrayerToList`. Must run
 * inside a transaction covering prayers, prayerListItems and
 * prayerHashtags. Returns whether there's anything to push.
 */
const deletePrayerLocal = async (existing: LocalPrayer): Promise<boolean> => {
  // the server cascades a list's delete to its items, so none of them need
  // their own push - drop them all locally now (a never-synced one just
  // vanishes, a synced one is covered by the cascade)
  await db.prayerListItems.where('prayerListId').equals(existing.id).delete();

  if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
    // never reached the server - just vanish, no network call
    await db.prayers.delete(existing.id);
    await dropUnreferencedPlaceholders(existing.hashtagIds ?? []);
    return false;
  }

  if (existing.syncStatus === 'pendingDelete') return false;

  await db.prayers.update(existing.id, { syncStatus: 'pendingDelete' });
  return true;
};

/**
 * Local half of deleting a list item (no trigger) - shared by
 * `queueLocalPrayerListItemDelete` and `queueLocalDetachListItem`. Must run
 * inside a transaction covering prayers and prayerListItems. Returns
 * whether there's anything to push.
 */
const deleteListItemLocal = async (existing: LocalPrayerListItem): Promise<boolean> => {
  await touchListDate(existing.prayerListId);

  if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
    await db.prayerListItems.delete(existing.id);
    return false;
  }

  if (existing.syncStatus === 'pendingDelete') return false;

  await db.prayerListItems.update(existing.id, { syncStatus: 'pendingDelete' });
  return true;
};

export interface NewLocalPrayer {
  text: string;
  isList: boolean;
  targetDate?: string;
  tags?: string[];
}

export const queueLocalPrayerCreate = async (prayer: NewLocalPrayer): Promise<string> => {
  const hashtagIds =
    prayer.tags && prayer.tags.length > 0 ? await resolveOrCreateLocalPrayerHashtagIds(prayer.tags) : undefined;

  const timestamp = now();
  const localPrayer: LocalPrayer = {
    id: uuid(),
    text: prayer.text,
    isList: prayer.isList,
    completed: false,
    date: timestamp,
    updateDate: timestamp,
    targetDate: prayer.targetDate,
    hashtagIds,
    syncStatus: 'pendingCreate',
  };

  await db.prayers.add(localPrayer);
  triggerGlobalSync(PRAYER_SYNC_KEY);
  return localPrayer.id;
};

export interface LocalPrayerChanges {
  text?: string;
  completed?: boolean;
  // `null` clears the target date; `undefined` leaves it unchanged
  targetDate?: string | null;
  tags?: string[];
}

/**
 * Note that any update - even tags-only - is pushed as a full PUT, which
 * (like any edit made through the API) moves the item's `date`; `date` is
 * bumped locally to match.
 */
export const queueLocalPrayerUpdate = async (id: string, changes: LocalPrayerChanges): Promise<void> => {
  const updated = await db.transaction('rw', db.prayers, db.prayerHashtags, async () => {
    const existing = await db.prayers.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const hashtagIds =
      changes.tags !== undefined ? await resolveOrCreateLocalPrayerHashtagIds(changes.tags) : existing.hashtagIds;
    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';
    const timestamp = now();

    await db.prayers.update(id, {
      text: changes.text ?? existing.text,
      completed: changes.completed ?? existing.completed,
      targetDate: changes.targetDate === null ? undefined : (changes.targetDate ?? existing.targetDate),
      hashtagIds: hashtagIds && hashtagIds.length > 0 ? hashtagIds : undefined,
      date: timestamp,
      updateDate: timestamp,
      syncStatus: nextStatus,
    });

    if (changes.tags !== undefined) {
      await dropUnreferencedPlaceholders((existing.hashtagIds ?? []).filter((tagId) => !hashtagIds!.includes(tagId)));
    }
    return true;
  });

  if (updated) triggerGlobalSync(PRAYER_SYNC_KEY);
};

export const queueLocalPrayerDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.prayers, db.prayerListItems, db.prayerHashtags, async () => {
    const existing = await db.prayers.get(id);
    if (!existing) return false;

    return deletePrayerLocal(existing);
  });

  if (shouldSync) triggerGlobalSync(PRAYER_SYNC_KEY);
};

export interface NewLocalPrayerListItem {
  itemText: string;
  targetDate?: string;
}

/**
 * @returns the new list item's id, or `undefined` if `listId` isn't a
 * (non-deleted) local list
 */
export const queueLocalPrayerListItemCreate = async (
  listId: string,
  item: NewLocalPrayerListItem
): Promise<string | undefined> => {
  const newId = await db.transaction('rw', db.prayers, db.prayerListItems, async () => {
    const list = await db.prayers.get(listId);
    if (!list || !list.isList || list.syncStatus === 'pendingDelete') return undefined;

    const localItem: LocalPrayerListItem = {
      id: uuid(),
      prayerListId: listId,
      itemText: item.itemText,
      targetDate: item.targetDate,
      completed: false,
      updateDate: now(),
      syncStatus: 'pendingCreate',
    };
    await db.prayerListItems.add(localItem);
    await touchListDate(listId);
    return localItem.id;
  });

  if (newId) triggerGlobalSync(PRAYER_SYNC_KEY);
  return newId;
};

export interface LocalPrayerListItemChanges {
  itemText?: string;
  completed?: boolean;
  // `null` clears the target date; `undefined` leaves it unchanged
  targetDate?: string | null;
  // moves the item to another list - must be a local, non-deleted list
  prayerListId?: string;
}

export const queueLocalPrayerListItemUpdate = async (
  id: string,
  changes: LocalPrayerListItemChanges
): Promise<void> => {
  const updated = await db.transaction('rw', db.prayers, db.prayerListItems, async () => {
    const existing = await db.prayerListItems.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const targetListId = changes.prayerListId ?? existing.prayerListId;
    if (targetListId !== existing.prayerListId) {
      const targetList = await db.prayers.get(targetListId);
      if (!targetList || !targetList.isList || targetList.syncStatus === 'pendingDelete') return false;
    }

    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.prayerListItems.update(id, {
      itemText: changes.itemText ?? existing.itemText,
      completed: changes.completed ?? existing.completed,
      targetDate: changes.targetDate === null ? undefined : (changes.targetDate ?? existing.targetDate),
      prayerListId: targetListId,
      // the update endpoint validates that an updateDate is present but never
      // reads it - the server sets its own on save, adopted after the push
      updateDate: now(),
      syncStatus: nextStatus,
    });
    await touchListDate(existing.prayerListId);
    if (targetListId !== existing.prayerListId) await touchListDate(targetListId);
    return true;
  });

  if (updated) triggerGlobalSync(PRAYER_SYNC_KEY);
};

export const queueLocalPrayerListItemDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.prayers, db.prayerListItems, async () => {
    const existing = await db.prayerListItems.get(id);
    if (!existing) return false;

    return deleteListItemLocal(existing);
  });

  if (shouldSync) triggerGlobalSync(PRAYER_SYNC_KEY);
};

/**
 * Offline equivalent of the server's attach endpoint, composed from
 * primitives: a new list item (client-generated id) carrying the prayer's
 * text and target date, plus a delete of the source prayer. As with the
 * server endpoint, the prayer's tags don't carry over (list items have
 * none), and the new item starts uncompleted. Push ordering (see
 * `pushPendingPrayers`) lands the new item before the source is deleted.
 *
 * @returns the new list item's id, or `undefined` if the source isn't a
 * non-list prayer or the target isn't a list
 */
export const queueLocalAttachPrayerToList = async (prayerId: string, listId: string): Promise<string | undefined> => {
  const newId = await db.transaction('rw', db.prayers, db.prayerListItems, db.prayerHashtags, async () => {
    const source = await db.prayers.get(prayerId);
    const list = await db.prayers.get(listId);
    if (!source || source.isList || source.syncStatus === 'pendingDelete') return undefined;
    if (!list || !list.isList || list.syncStatus === 'pendingDelete') return undefined;

    const localItem: LocalPrayerListItem = {
      id: uuid(),
      prayerListId: listId,
      itemText: source.text,
      targetDate: source.targetDate,
      completed: false,
      updateDate: now(),
      syncStatus: 'pendingCreate',
    };
    await db.prayerListItems.add(localItem);
    await deletePrayerLocal(source);
    return localItem.id;
  });

  if (newId) triggerGlobalSync(PRAYER_SYNC_KEY);
  return newId;
};

/**
 * Offline equivalent of the server's detach endpoint: a new (uncompleted,
 * untagged) prayer carrying the list item's text and target date, plus a
 * delete of the list item.
 *
 * @returns the new prayer's id, or `undefined` if the item doesn't exist
 */
export const queueLocalDetachListItem = async (itemId: string): Promise<string | undefined> => {
  const newId = await db.transaction('rw', db.prayers, db.prayerListItems, async () => {
    const source = await db.prayerListItems.get(itemId);
    if (!source || source.syncStatus === 'pendingDelete') return undefined;

    const timestamp = now();
    const localPrayer: LocalPrayer = {
      id: uuid(),
      text: source.itemText,
      isList: false,
      completed: false,
      date: timestamp,
      updateDate: timestamp,
      targetDate: source.targetDate,
      syncStatus: 'pendingCreate',
    };
    await db.prayers.add(localPrayer);
    await deleteListItemLocal(source);
    return localPrayer.id;
  });

  if (newId) triggerGlobalSync(PRAYER_SYNC_KEY);
  return newId;
};
